import { useScrollReveal, useScrollRevealGroup } from '@/hooks/useScrollReveal';

const channels = [
  {
    name: 'Telegram',
    description: 'Основной канал коммуникации. AIGAM подключается к вашему Telegram-аккаунту и обрабатывает все входящие сообщения, заявки и лиды в едином потоке.',
    features: ['Приём заявок из чатов', 'Исходящие AI-диалоги', 'Автоответчик 24/7', 'Пересылка в CRM'],
    icon: 'ri-telegram-line',
  },
  {
    name: 'WhatsApp',
    description: 'Интеграция через WhatsApp Business API. Клиенты получают сообщения в привычном мессенджере, а вы — готовые лиды в CRM.',
    features: ['WhatsApp Business API', 'Шаблоны сообщений', 'AI-диалоги', 'Медиа-файлы'],
    icon: 'ri-whatsapp-line',
  },
  {
    name: 'MAX',
    description: 'Поддержка платформы MAX для охвата дополнительной аудитории. Все входящие обращения автоматически попадают в единую очередь.',
    features: ['Приём обращений', 'AI-квалификация', 'Передача в CRM', 'Аналитика'],
    icon: 'ri-smartphone-line',
  },
];

const crms = [
  { name: 'amoCRM', description: 'Полная двусторонняя синхронизация: сделки, контакты, задачи. Заявки попадают в воронку автоматически.' },
  { name: 'Bitrix24', description: 'Интеграция с CRM, задачами и чатами. Все коммуникации сохраняются в карточке сделки.' },
];

const features = [
  {
    title: 'Единый поток заявок',
    description: 'Все заявки из Telegram, WhatsApp и MAX попадают в единую очередь. Ни один лид не теряется.',
    icon: 'ri-stack-line',
  },
  {
    title: 'Сквозная аналитика',
    description: 'Отслеживайте путь клиента от первого контакта до сделки. Все каналы в одном дашборде.',
    icon: 'ri-line-chart-line',
  },
  {
    title: 'Автомаршрутизация',
    description: 'Настройте правила распределения лидов по менеджерам в зависимости от источника, темы и приоритета.',
    icon: 'ri-direction-line',
  },
  {
    title: 'История коммуникаций',
    description: 'Полная история всех диалогов сохраняется и доступна в карточке клиента в CRM.',
    icon: 'ri-history-line',
  },
];

export default function IntPlatforms() {
  const { ref: labelRef, revealed: labelRevealed } = useScrollReveal();
  const { ref: titleRef, revealed: titleRevealed } = useScrollReveal({ delay: 150 });
  const { containerRef: channelsRef, revealedCount: channelsRevealed } = useScrollRevealGroup<HTMLDivElement>(3);
  const { ref: crmTitleRef, revealed: crmTitleRevealed } = useScrollReveal();
  const { ref: crmDescRef, revealed: crmDescRevealed } = useScrollReveal({ delay: 100 });
  const { containerRef: crmsRef, revealedCount: crmsRevealed } = useScrollRevealGroup<HTMLDivElement>(2);
  const { ref: featuresTitleRef, revealed: featuresTitleRevealed } = useScrollReveal();
  const { containerRef: featuresRef, revealedCount: featuresRevealed } = useScrollRevealGroup<HTMLDivElement>(4);

  return (
    <section id="int-platforms" className="py-20 md:py-28 bg-background-50">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10">
        {/* Каналы */}
        <div className="mb-16 md:mb-20">
          <div className="mb-10 md:mb-14 max-w-[640px]">
            <span ref={labelRef} className={`text-[12px] font-semibold uppercase tracking-[0.18em] text-primary-600 reveal-fade-up ${labelRevealed ? 'revealed' : ''}`}>
              Платформы
            </span>
            <h2 ref={titleRef} className={`mt-4 font-display font-extrabold text-[34px] md:text-[46px] leading-[1.1] tracking-tightest text-foreground-950 reveal-fade-up ${titleRevealed ? 'revealed' : ''}`}>
              Все каналы продаж
              <br />
              в одной системе
            </h2>
          </div>

          <div ref={channelsRef} className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {channels.map((ch, i) => (
              <div
                key={ch.name}
                className={`rounded-2xl bg-background-100 border border-background-200 p-6 md:p-7 hover:border-primary-500/40 transition-all duration-300 reveal-fade-up ${i < channelsRevealed ? 'revealed' : ''}`}
                style={i < channelsRevealed ? { transitionDelay: `${i * 120}ms` } : undefined}
              >
                <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center text-primary-600 mb-4">
                  <i className={`${ch.icon} text-lg`}></i>
                </div>
                <h3 className="font-display font-bold text-[18px] text-foreground-950 mb-2">
                  {ch.name}
                </h3>
                <p className="text-[13px] text-foreground-600 leading-relaxed mb-4">
                  {ch.description}
                </p>
                <ul className="space-y-1.5">
                  {ch.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-[13px] text-foreground-700">
                      <span className="w-1 h-1 rounded-full bg-primary-400 mt-1.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* CRM */}
        <div className="mb-16 md:mb-20">
          <h3 ref={crmTitleRef} className={`font-display font-extrabold text-[20px] md:text-[24px] text-foreground-950 mb-1 reveal-fade-up ${crmTitleRevealed ? 'revealed' : ''}`}>
            CRM-интеграции
          </h3>
          <p ref={crmDescRef} className={`text-[14.5px] text-foreground-500 mb-2 max-w-[560px] reveal-fade-up ${crmDescRevealed ? 'revealed' : ''}`}>
            Готовые лиды автоматически попадают в вашу CRM со всей историей переписки и результатами квалификации.
          </p>
          <p className="text-[13px] font-medium text-primary-600 mb-8">
            <i className="ri-gift-line mr-1"></i>
            Подключение к этим CRM — бесплатно
          </p>

          <div ref={crmsRef} className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
            {crms.map((crm, i) => (
              <div
                key={crm.name}
                className={`rounded-2xl bg-background-100 border border-primary-200/40 p-5 md:p-6 relative hover:border-primary-500/60 transition-all duration-300 reveal-fade-up ${i < crmsRevealed ? 'revealed' : ''}`}
                style={i < crmsRevealed ? { transitionDelay: `${i * 150}ms` } : undefined}
              >
                <span className="absolute -top-2.5 right-4 px-3 py-0.5 text-[10px] font-semibold rounded-full bg-primary-500 text-background-50 whitespace-nowrap">
                  Бесплатно
                </span>
                <div className="flex items-center gap-3 mb-2">
                  <span className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center text-primary-600 shrink-0">
                    <i className="ri-database-2-line text-sm"></i>
                  </span>
                  <h4 className="font-heading text-base font-semibold text-foreground-950">
                    {crm.name}
                  </h4>
                </div>
                <p className="text-[13px] text-foreground-600 leading-relaxed">{crm.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Особенности */}
        <div>
          <h3 ref={featuresTitleRef} className={`font-display font-extrabold text-[20px] md:text-[24px] text-foreground-950 mb-1 reveal-fade-up ${featuresTitleRevealed ? 'revealed' : ''}`}>
            Возможности платформы
          </h3>
          <p className="text-[14.5px] text-foreground-500 mb-8 max-w-[560px]">
            Интеграции AIGAM — это не просто коннекторы, а полноценная система управления продажами.
          </p>

          <div ref={featuresRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {features.map((f, i) => (
              <div
                key={f.title}
                className={`rounded-2xl bg-background-100 border border-background-200 p-5 md:p-6 hover:border-primary-500/40 transition-all duration-300 reveal-fade-up ${i < featuresRevealed ? 'revealed' : ''}`}
                style={i < featuresRevealed ? { transitionDelay: `${i * 100}ms` } : undefined}
              >
                <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center text-primary-600 mb-3">
                  <i className={`${f.icon} text-base`}></i>
                </div>
                <h4 className="font-heading text-sm font-semibold text-foreground-950 mb-1.5">
                  {f.title}
                </h4>
                <p className="text-[13px] text-foreground-600 leading-relaxed">{f.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}